import React, { useEffect, FunctionComponent, useState } from "react";
import { IDigitalShareItem } from "../../../../Models/IDigitalShareItem";
import { DigitalShareItemType } from "../../../../Enums/DigitalShareItemType";
import {
    Backdrop,
    CloseButton,
    Dialog,
    Footer,
    Header,
    MainContent,
    Title,
    TrainingContainer,
    TrainingDescription,
    TrainingHeaderText,
    TrainingOption,
    TrainingVideoContainer,
} from "./DialogueStyles";

import { Button } from "../../Button/Button";
import { FiArrowRight } from "@react-icons/all-files/fi/FiArrowRight";
import { FiArrowLeft } from "@react-icons/all-files/fi/FiArrowLeft";
import { TextButton } from "../../Button/TextButton";
import { DialogueOutputs } from "./DialogueOutputs";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { IRootReducer } from "../../../../Models/IRootReducer";
import { setDialogTrainingState } from "../../../../Actions/DigitalShareActions";
import { DigitalShareAppType } from "../../../../Enums/DigitalShareAppType";

export enum DialogTrainingState {
  LoopNet,
  Crexi,
  None,
}

export interface IDigitalShareDialogProps {
  onClose: () => void;
  onSubmit: (outputs: DigitalShareItemType[]) => void;
  outputs: IDigitalShareItem[];
  app: DigitalShareAppType;
}

const LOOP_NET_VIDEO_URL
  = "/Images/Match/Videos/BUX%20-%20LoopNet%20In%20App%20v3.mp4";
const LOOP_NET_PDF_URL = "/Images/buxton-platform-share-v2.pdf";
const LOOP_NET_VIDEO_DESCRIPTION
  = "Set your property apart from the competition by adding these insights to CoStar, LoopNet, Crexi, and other listing sites. Watch the video or read the PDF instructions to learn how.";

const DigitalShareDialog: FunctionComponent<IDigitalShareDialogProps> = (
    props: IDigitalShareDialogProps
) => {
    const [outputs, setOutputs] = useState<IDigitalShareItem[]>([]);
    const [trainingRendered, setTrainingRendered] = useState<boolean>(true);
    const [outputSections, setOutputSections] = useState<string[]>([]);

    const trainingState = useSelector(
        (root: IRootReducer): DialogTrainingState => {
            return root.digitalShare.dialogTrainingState;
        },
        shallowEqual
    );

    const dispatch = useDispatch();
    useEffect(() => {
        const sections: Set<string> = new Set(
            Array.from(props.outputs, (o) => o.sectionTitle)
        );

        setOutputSections(Array.from(sections));
        setOutputs(props.outputs);
    }, []);

    const onLoopnetClick = () => {
        dispatch(setDialogTrainingState(DialogTrainingState.LoopNet));
    };

    const onCrexiClick = () => {
        dispatch(setDialogTrainingState(DialogTrainingState.Crexi));
    };

    return (
        <>
            <Backdrop />
            <Dialog data-testid={"digital-share-dialog"}>
                <Header>
                    <Title>Export Sections to Share</Title>
                    <CloseButton onClick={() => props.onClose()}>X</CloseButton>
                </Header>
                <MainContent className={!trainingRendered ? "full-width" : null}>
                    {trainingRendered && (
                        <TrainingContainer>
                            <Header style={{ height: 45, borderBottom: "none" }}>
                                <TrainingHeaderText>
                                    <TrainingOption
                                        title="How To Share on Loopnet"
                                        clickCallback={onLoopnetClick}
                                        selected={trainingState === DialogTrainingState.LoopNet}
                                    />
                                    <TrainingOption
                                        title="How To Share on Crexi"
                                        clickCallback={onCrexiClick}
                                        selected={trainingState === DialogTrainingState.Crexi}
                                    />
                                </TrainingHeaderText>
                                {trainingRendered ? (
                                    <FiArrowLeft
                                        size="18"
                                        onClick={() => setTrainingRendered(false)}
                                    />
                                ) : (
                                    <FiArrowRight
                                        size="18"
                                        onClick={() => setTrainingRendered(true)}
                                    />
                                )}
                            </Header>
                            {trainingState === DialogTrainingState.LoopNet && (
                                <>
                                    <TrainingVideoContainer>
                                        <video controls>
                                            <source src={LOOP_NET_VIDEO_URL} type="video/mp4" />
                                            <source src="movie.ogg" type="video/ogg" />
                                        </video>
                                    </TrainingVideoContainer>
                                    <TrainingDescription>
                                        <div style={{ fontWeight: 900, marginBottom: "5px" }}>
                      Sharing on Loopnet
                                        </div>
                                        {LOOP_NET_VIDEO_DESCRIPTION}
                                        <a
                                            style={{ textAlign: "center" }}
                                            href={LOOP_NET_PDF_URL}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            <TextButton title="View PDF Instructions" />
                                        </a>
                                    </TrainingDescription>
                                </>
                            )}

                            {trainingState === DialogTrainingState.Crexi && (
                                <>
                                    <TrainingVideoContainer>
                                        <video controls>
                                            <source src={LOOP_NET_VIDEO_URL} type="video/mp4" />
                                            <source src="movie.ogg" type="video/ogg" />
                                        </video>
                                    </TrainingVideoContainer>
                                    <TrainingDescription>
                                        <div style={{ fontWeight: 900, marginBottom: "5px" }}>
                      Sharing on Crexi
                                        </div>
                                        {LOOP_NET_VIDEO_DESCRIPTION}
                                        <a
                                            style={{ textAlign: "center" }}
                                            href={LOOP_NET_PDF_URL}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            <TextButton title="View PDF Instructions" />
                                        </a>
                                    </TrainingDescription>
                                </>
                            )}
                        </TrainingContainer>
                    )}
                    {trainingRendered && (
                        <DialogueOutputs
                            outputs={outputs}
                            outputSections={outputSections}
                            onOutputsChanged={setOutputs}
                            isTrainingRendered={true}
                        />
                    )}
                    {!trainingRendered && (
                        <DialogueOutputs
                            onOutputsChanged={setOutputs}
                            outputs={outputs}
                            outputSections={outputSections}
                            isTrainingRendered={false}
                            onSetTrainingState={setTrainingRendered}
                        />
                    )}
                </MainContent>
                <Footer>
                    <Button
                        title="Cancel"
                        color="rgba(0, 0, 0, 0.65)"
                        style={{ marginRight: "12px" }}
                        data-testclass={"digital-share-dialogue-cancel"}
                        clickCallback={() => props.onClose()}
                    />
                    <Button
                        title="Export"
                        bgColor="#2b8b49"
                        color="#ffffff"
                        data-testclass={"digital-share-dialog-submit"}
                        disabled={outputs.filter((o, i) => o.isSelected).length === 0}
                        clickCallback={(e: any) => {
                            e.stopPropagation();
                            e.preventDefault();
                            const selected = outputs
                                .filter((o, i) => o.isSelected)
                                .map((o) => o.type);
                            props.onSubmit(selected);
                        }}
                    />
                </Footer>
            </Dialog>
        </>
    );
};

export default React.memo(DigitalShareDialog);
